import { store, type RootState } from '../app/store';
import {
  addDangerAlert,
  addInfoAlert,
  addSuccessAlert,
  addWarningAlert,
  type AlertsState,
} from '../feature/alertsSlice';
import type { IAlertService } from './alert-service';

export type AlertSeverity = 'info' | 'success' | 'warning' | 'danger';

export interface Alert {
  message: string;
  severity: AlertSeverity;
}

type AlertsChangeCallback = (alerts: Alert[]) => void;

class ReduxBackedAlertStore implements IAlertService {
  public alerts: Alert[];

  private subscribers: Set<AlertsChangeCallback>;

  private unsubscribeFromStore: (() => void) | null;

  constructor() {
    const state: RootState = store.getState();
    this.alerts = this.selectAlerts(state);
    this.subscribers = new Set<AlertsChangeCallback>();
    this.unsubscribeFromStore = store.subscribe(this.handleStoreChange);
  }

  public onChange(cb: AlertsChangeCallback): void {
    this.subscribers.add(cb);
    cb(this.alerts);
  }

  public info(message: string, clearPrevious: boolean = true): void {
    this.logAdd('info', message);
    if (clearPrevious) {
      this.clearAll();
    }
    store.dispatch(addInfoAlert(message));
  }

  public success(message: string, clearPrevious: boolean = true): void {
    this.logAdd('success', message);
    if (clearPrevious) {
      this.clearAll();
    }
    store.dispatch(addSuccessAlert(message));
  }

  public warning(message: string, clearPrevious: boolean = true): void {
    this.logAdd('warning', message);
    if (clearPrevious) {
      this.clearAll();
    }
    store.dispatch(addWarningAlert(message));
  }

  public error(message: string, clearPrevious: boolean = true): void {
    this.logAdd('danger', message);
    if (clearPrevious) {
      this.clearAll();
    }
    store.dispatch(addDangerAlert(message));
  }

  public addAlert(message: string, severity: AlertSeverity, clearPrevious: boolean = true): void {
    switch (severity) {
      case 'info':
        this.info(message, clearPrevious);
        break;
      case 'success':
        this.success(message, clearPrevious);
        break;
      case 'warning':
        this.warning(message, clearPrevious);
        break;
      case 'danger':
        this.error(message, clearPrevious);
        break;
      default:
        // eslint-disable-next-line no-console
        console.warn(`Unknown alert severity: ${severity}, defaulting to info.`);
        this.info(message, clearPrevious);
    }
  }

  public clear(alert: Alert): void {
    const currentAlerts = this.alerts;
    const index = currentAlerts.indexOf(alert);
    if (index === -1) {
      return;
    }
    const updated = [...currentAlerts.slice(0, index), ...currentAlerts.slice(index + 1)];
    this.updateLocalAlerts(updated);
  }

  public clearAll(): void {
    if (this.alerts.length > 0) {
      // eslint-disable-next-line no-console
      console.log(`Clearing ${this.alerts.length} alerts`);
    }
    this.updateLocalAlerts([]);
  }

  public dispose(): void {
    this.unsubscribeFromStore?.();
    this.unsubscribeFromStore = null;
    this.subscribers.clear();
  }

  private readonly handleStoreChange = (): void => {
    const state: RootState = store.getState();
    const nextAlerts = this.selectAlerts(state);
    if (nextAlerts === this.alerts) {
      return;
    }
    this.updateLocalAlerts(nextAlerts);
  };

  private updateLocalAlerts(nextAlerts: Alert[]): void {
    this.alerts = nextAlerts;
    this.subscribers.forEach((cb) => cb(this.alerts));
  }

  private selectAlerts(state: RootState): Alert[] {
    const slice: AlertsState = state.alerts;
    return slice?.alerts ?? [];
  }

  private logAdd(severity: AlertSeverity, message: string): void {
    // eslint-disable-next-line no-console
    console.log(`Adding ${severity} alert: ${message}`);
  }
}

export const alertStore: IAlertService = new ReduxBackedAlertStore();
